"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Award, CircleCheck as CheckCircle2, Clock, PhoneCall, ShieldCheck, Star, Wrench, Zap, CircleQuestionMark as HelpCircle, Flame } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
const trustBadges = [{
  title: "Certified Bradford White Specialists",
  description: "Factory-trained technicians experienced with all residential and commercial models.",
  iconName: "award"
}, {
  title: "15+ Years Local Experience",
  description: "Serving Jonesboro, GA and Clayton County homeowners with prompt, honest repairs.",
  iconName: "clock"
}, {
  title: "OEM Parts & Hydrojet System Ready",
  description: "We use genuine Bradford White components to protect your factory warranty.",
  iconName: "wrench"
}, {
  title: "24/7 Emergency Response",
  description: "Rapid dispatch across Jonesboro for sudden tank bursts, pilot issues, and leaks.",
  iconName: "zap"
}];
const faqs = [{
  id: "item-1",
  question: "Why does my Bradford White water heater keep shutting off?",
  answer: "The most common culprit is a tripped thermal switch, a faulty thermocouple, or a dirty air intake screen on the Defender Safety System. Our licensed plumbers carry original Bradford White replacement parts to diagnose and solve the issue on the spot."
}, {
  id: "item-2",
  question: "What makes Bradford White water heaters different from retail store brands?",
  answer: "Bradford White water heaters are professional-grade units manufactured exclusively for licensed plumbing contractors. They feature the Hydrojet Total Performance System to reduce sediment buildup and Vitraglas enamel lining for maximum corrosion protection."
}, {
  id: "item-3",
  question: "Can you perform warranty repairs on Bradford White units in Jonesboro?",
  answer: "Yes. Jonesboro Emergency Plumber verifies serial numbers and handles warranty claims for eligible Bradford White tanks, covering certified labor, burner assemblies, gas control valves, and electrical heating elements."
}, {
  id: "item-4",
  question: "How long does a Bradford White water heater typically last in Georgia?",
  answer: "With routine annual maintenance and anode rod checks, a Bradford White tank typically delivers 10 to 14 reliable years. Clayton County water conditions can cause mineral buildup, making regular flushes essential."
}, {
  id: "item-5",
  question: "Do you offer same-day replacement if my Bradford White tank is leaking?",
  answer: "Yes. We maintain emergency stock of atmospheric gas, power vent, and electric Bradford White models for rapid same-day installation and code-compliant disposal of your old unit."
}];
const testimonials = [{
  id: 1,
  name: "Marcus T.",
  location: "Jonesboro, GA",
  rating: 5,
  quote: "Our Bradford White gas heater started leaking from the bottom on a Sunday morning. The team arrived in 40 minutes, explained our warranty options, and had a new unit running by afternoon.",
  initials: "MT"
}, {
  id: 2,
  name: "Elena R.",
  location: "Clayton County",
  rating: 5,
  quote: "They are clearly experts on Bradford White systems. Fixed the pilot light sensor and flushed the sediment quickly. Fair pricing and zero runaround.",
  initials: "ER"
}, {
  id: 3,
  name: "David K.",
  location: "Jonesboro, GA",
  rating: 5,
  quote: "Genuine OEM parts and true factory knowledge. Jonesboro Emergency Plumber solved a pressure valve mystery that another local contractor couldn't diagnose.",
  initials: "DK"
}];
export default function BradfordTrust() {
  const shouldReduceMotion = useReducedMotion();
  const [activeFaq, setActiveFaq] = useState<string>("item-1");
  const getBadgeIcon = (name: string) => {
    switch (name) {
      case "award":
        return <Award className="size-6 text-accent" />;
      case "clock":
        return <Clock className="size-6 text-accent" />;
      case "wrench":
        return <Wrench className="size-6 text-accent" />;
      case "zap":
        return <Zap className="size-6 text-accent" />;
      default:
        return <ShieldCheck className="size-6 text-accent" />;
    }
  };
  return <section id="bradfordwhitefaqandtrust" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-semibold text-secondary-foreground">
            <Flame className="size-3.5 mr-1 text-accent" />
            Bradford White Factory Expertise
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Bradford White Questions & Proven Trust
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Get straightforward answers about Bradford White water heaters, discover our local certifications, and see why Jonesboro homeowners rely on our 24/7 service.
          </p>
        </div>

        {/* Trust Badges Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {trustBadges.map((badge, index) => <motion.div key={badge.title} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: index * 0.1
        }}>
              <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    {getBadgeIcon(badge.iconName)}
                  </div>
                  <CardTitle className="text-lg font-default font-semibold text-card-foreground">
                    {badge.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground leading-normal">
                    {badge.description}
                  </p>
                </CardContent>
              </Card>
            </motion.div>)}
        </div>

        {/* Main Content Split: FAQs & Testimonials/Callout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          
          {/* FAQ Column */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-6">
              <HelpCircle className="size-6 text-primary" />
              <h3 className="text-2xl font-default font-bold text-foreground">
                Frequently Asked Questions
              </h3>
            </div>
            
            <Accordion type="single" collapsible value={activeFaq} onValueChange={setActiveFaq} className="w-full bg-card rounded-xl border border-border p-2 md:p-4 shadow-sm">
              {faqs.map((faq, index) => <div key={faq.id} data-index={index}>
                  <AccordionItem value={faq.id} className="border-border/60 px-2 py-1">
                    <AccordionTrigger className="text-base font-semibold text-foreground hover:text-primary transition-colors py-4">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>

            <div className="mt-8 p-6 rounded-xl bg-secondary/50 border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="font-default font-semibold text-foreground text-base">
                  Have an urgent water heater problem?
                </h4>
                <p className="text-sm text-muted-foreground">
                  Our Jonesboro technicians carry Bradford White sensors, valves, and elements in service vans.
                </p>
              </div>
              <Button asChild size="lg" className="w-full sm:w-auto font-semibold">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  Call 24/7 Dispatch
                </Link>
              </Button>
            </div>
          </div>

          {/* Testimonials & Certification Trust Card Column */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Trust & Guarantee Box */}
            <Card className="border-border bg-primary text-primary-foreground shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="size-6 text-accent" />
                  <CardTitle className="text-xl font-default font-bold text-primary-foreground">
                    Jonesboro Plumber Guarantee
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-primary-foreground/90">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span>Official Bradford White factory specifications honored on all repair tasks.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span>Licensed, bonded, and insured master plumbing specialists for Georgia properties.</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                  <span>Upfront pricing with no hidden charges before any repair or install begins.</span>
                </div>
              </CardContent>
            </Card>

            {/* Testimonials List */}
            <div className="space-y-4">
              <h3 className="text-xl font-default font-bold text-foreground">
                Customer Experiences
              </h3>

              {testimonials.map((review, index) => <div key={review.id} data-index={index}>
                  <Card className="border border-border bg-card shadow-sm p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <SafeHydrate><Avatar className="size-9 border border-border">
                          <AvatarFallback className="bg-secondary text-secondary-foreground font-semibold text-xs">
                            {review.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div>
                          <div className="text-sm font-semibold text-card-foreground">
                            {review.name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {review.location}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5" aria-label="5 stars rating">
                        {Array.from({
                      length: review.rating
                    }).map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-3.5 fill-accent text-accent" />)}
                      </div>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground italic leading-relaxed">
                      "{review.quote}"
                    </p>
                  </Card>
                </div>)}
            </div>

            {/* Explore links */}
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Button asChild variant="outline" size="sm">
                <Link to="/water-heater-repair/">
                  Water Heater Repair
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/water-heater-replacement/">
                  Replacement Options
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/brands/">
                  View All Brands
                </Link>
              </Button>
            </div>

          </div>

        </div>
      </div>
    </section>;
}